/**
 * Strip session fields that duplicate program (variation) defaults so publish/bulk
 * matches the session modal's prepareSubmissionValues + isEqualToProgramValue behavior.
 */

export type DecimalEnumMap = Record<string, { value?: number } | undefined>;

function findMatchingAlternative(
  variation: any,
  numDaysOverride: number | null | undefined,
  decimalEnums: DecimalEnumMap
): any | null {
  const alts = variation?.regularOfferIncrementAlternatives;
  if (!Array.isArray(alts) || numDaysOverride == null) return null;
  for (const alt of alts) {
    const enumItem = decimalEnums[alt.key];
    if (enumItem && enumItem.value === numDaysOverride) return alt;
  }
  return null;
}

function sessionLengthSelectorVisible(variation: any): boolean {
  let total = 0;
  if (variation?.regularOfferIncrementKey) total += 1;
  const alts = variation?.regularOfferIncrementAlternatives;
  if (Array.isArray(alts)) total += alts.length;
  return total > 1;
}

function normalizeTierPrice(n: unknown): number | null {
  return typeof n === "number" && !Number.isNaN(n) ? n : null;
}

function isNumDaysEqualToProgram(
  currentValue: number | null | undefined,
  variation: any,
  decimalEnums: DecimalEnumMap
): boolean {
  if (currentValue === variation?.numDays) return true;
  const key = variation?.regularOfferIncrementKey;
  if (key && decimalEnums[key] != null) {
    const v = decimalEnums[key]!.value;
    if (v === currentValue) return true;
  }
  return false;
}

function isPriceOverrideEqualToProgram(
  currentValue: number | null | undefined,
  variation: any,
  numDaysOverride: number | null | undefined,
  decimalEnums: DecimalEnumMap
): boolean {
  if (currentValue === variation?.priceRegular) return true;
  const alt = findMatchingAlternative(variation, numDaysOverride, decimalEnums);
  if (alt && currentValue === alt.price) return true;
  return false;
}

function isPriceNoteEqualToProgram(
  currentValue: string | null | undefined,
  variation: any,
  numDaysOverride: number | null | undefined,
  decimalEnums: DecimalEnumMap
): boolean {
  const base = variation?.priceNote || "";
  if (currentValue === base) return true;
  const alts = variation?.regularOfferIncrementAlternatives;
  if (!Array.isArray(alts) || numDaysOverride == null) return false;
  for (const alternative of alts) {
    const enumItem = decimalEnums[alternative.key];
    const note = alternative.note || "";
    if (enumItem && enumItem.value === numDaysOverride && currentValue === note) return true;
  }
  return false;
}

function isTimingNoteEqualToProgram(currentValue: string | null | undefined, variation: any): boolean {
  const programValue = variation?.timings?.timingNote || "";
  return currentValue === programValue;
}

function isTier2EqualToProgram(
  currentValue: number | null | undefined,
  variation: any,
  numDaysOverride: number | null | undefined,
  decimalEnums: DecimalEnumMap
): boolean {
  const base = variation?.priceRegularTier2 ?? null;
  if (currentValue === base) return true;
  const alt = findMatchingAlternative(variation, numDaysOverride, decimalEnums);
  if (alt) {
    const altTier = alt.priceTier2 ?? null;
    if (currentValue === altTier) return true;
  }
  return false;
}

function isTier3EqualToProgram(
  currentValue: number | null | undefined,
  variation: any,
  numDaysOverride: number | null | undefined,
  decimalEnums: DecimalEnumMap
): boolean {
  const base = variation?.priceRegularTier3 ?? null;
  if (currentValue === base) return true;
  const alt = findMatchingAlternative(variation, numDaysOverride, decimalEnums);
  if (alt) {
    const altTier = alt.priceTier3 ?? null;
    if (currentValue === altTier) return true;
  }
  return false;
}

export interface NormalizeSessionOverridesInput {
  offer: {
    numDaysOverride?: number | null;
    priceOverride?: number | null;
    priceNoteOverride?: string | null;
    timingNoteOverride?: string | null;
    pricingTierPriceOverrides?: { v?: number; tier2?: number | null; tier3?: number | null } | null;
    myOfferIncrementKey?: string | null;
  };
  variation: any;
  decimalEnums: DecimalEnumMap;
}

export interface NormalizedSessionOverrides {
  numDaysOverride: number | null;
  priceOverride: number | null;
  priceNoteOverride: string | null;
  timingNoteOverride: string | null;
  pricingTierPriceOverrides: { v: 1; tier2?: number; tier3?: number } | null;
  myOfferIncrementKey: string | null;
}

/**
 * Returns override fields to merge onto offer/form data before prepareOfferData(…)
 * / prepareOfferDataServer(…), mirroring client prepareSubmissionValues.
 */
export function normalizeSessionOverrideFieldsForPublish(
  input: NormalizeSessionOverridesInput
): NormalizedSessionOverrides {
  const { offer, variation, decimalEnums } = input;

  /** Same order as client `prepareSubmissionValues`: match alts using stored numDays before clearing it. */
  const effectiveNumDays = offer.numDaysOverride ?? null;

  let numDaysOverride = effectiveNumDays;
  let priceOverride = offer.priceOverride ?? null;
  let priceNoteOverride = offer.priceNoteOverride ?? null;
  let timingNoteOverride = offer.timingNoteOverride ?? null;
  let pricingTierPriceOverrides: NormalizedSessionOverrides["pricingTierPriceOverrides"] = null;
  if (offer.pricingTierPriceOverrides != null) {
    pricingTierPriceOverrides = {
      ...offer.pricingTierPriceOverrides,
      v: 1,
    };
  }
  let myOfferIncrementKey = offer.myOfferIncrementKey ?? null;

  if (pricingTierPriceOverrides != null) {
    const pt: any = pricingTierPriceOverrides;
    const t2 = normalizeTierPrice(pt?.tier2);
    const t3 = normalizeTierPrice(pt?.tier3);
    if (isTier2EqualToProgram(t2, variation, effectiveNumDays, decimalEnums)) {
      pt.tier2 = null;
    }
    if (isTier3EqualToProgram(t3, variation, effectiveNumDays, decimalEnums)) {
      pt.tier3 = null;
    }
    const tier2 = normalizeTierPrice(pt?.tier2);
    const tier3 = normalizeTierPrice(pt?.tier3);
    const out: { v: 1; tier2?: number; tier3?: number } = { v: 1 };
    if (tier2 !== null) out.tier2 = tier2;
    if (tier3 !== null) out.tier3 = tier3;
    pricingTierPriceOverrides = Object.keys(out).length > 1 ? out : null;
  }

  if (isPriceOverrideEqualToProgram(priceOverride, variation, effectiveNumDays, decimalEnums)) {
    priceOverride = null;
  }

  if (isPriceNoteEqualToProgram(priceNoteOverride, variation, effectiveNumDays, decimalEnums)) {
    priceNoteOverride = null;
  }

  if (isTimingNoteEqualToProgram(timingNoteOverride, variation)) {
    timingNoteOverride = null;
  }

  if (isNumDaysEqualToProgram(numDaysOverride, variation, decimalEnums)) {
    numDaysOverride = null;
  }

  if (!sessionLengthSelectorVisible(variation)) {
    myOfferIncrementKey = null;
  }

  return {
    numDaysOverride,
    priceOverride,
    priceNoteOverride,
    timingNoteOverride,
    pricingTierPriceOverrides,
    myOfferIncrementKey,
  };
}
